import { fetchExperienceCategories, fetchExperienceMetrics } from 'actions/ExperienceActions'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import GeoContext from 'contexts/geoContext'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { getExperienceCarouselHeadingText } from 'constants/experience'
import { ChertoExperiments } from 'constants/experiments'
import useQueryParams from 'hooks/useQueryParams'
import useExperiment from 'hooks/Experiments/useExperiment'
import { waitElement } from 'lib/dom/waitElement'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperiencesPageSearchHeader from './ExperiencesPageSearchHeader'
import config from 'constants/config'
import ExperiencesPageSearchCarousel from './ExperiencesPageSearchCarousel'
import ExperiencesPageFeaturedCarousel from './ExperiencesPageFeaturedCarousel'
import ExperiencesPageRecentlyViewedExperiences from './ExperiencesPageRecentlyViewedExperiences'
import ExperiencesPageCategoriesCarousel from './ExperiencesPageCategoriesCarousel'
import ExperiencesPageLocationModal from './ExperiencesPageLocationModal/ExperiencesPageLocationModal'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import { useGlobalExperiencesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  useCategoryCarousels,
  getEditorPickLocations,
  getExperiencesPageLocations,
} from './ExperiencesPageHardcodedLists'
import SolidSearchIcon from 'LuxKit/Icons/solid/SolidSearchIcon'
import ExperiencesPageEditorsPicks from './ExperiencesPageEditorsPicks'
import ExperiencesPageBlogPostsCarousel from './ExperiencesPageBlogPostsCarousel'
import { SearchMenuStates } from 'components/Search/type'
import ExperiencesPageWhiteLabelBannerAd from './ExperiencesPageWhiteLabelBannerAd'
import ExperiencesPageUpcomingOrderCarousels from './ExperiencesPageUpcomingOrderCarousels'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import { fetchListBanners } from 'actions/ContentActions'
import useSmartExperiencePlace from '../hooks/useSmartExperiencePlace'
import { useResolvedPlaceIdentities } from 'hooks/usePlace'
import ModalContext from 'contexts/ModalContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { BANNER_LOCATION_EXPERIENCES } from 'constants/bannerLocations'
import ExperiencesTrendingDestinationsCarousel from './ExperiencesTrendingDestinationsCarousel'
import Group from 'components/utils/Group'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import ExperienceLuxPlusCarousel from '../ExperienceLists/ExperienceLuxPlusCarousel'
import ExperiencesPageCollectionsCarousels from './ExperiencesPageCollectionsCarousels'
import ExperiencesHomepageUpliftLayout from './ExperiencesHomepageUplift/ExperiencesHomepageUpliftLayout'
import ExperiencesHomepageMetrics from './ExperiencesHomepageUplift/ExperiencesHomepageMetrics'
import ExperiencesHomepagePersonalisation from './ExperiencesHomepageUplift/ExperiencesHomepagePersonalisation'
import ExperiencesHomepageRecentSearches from './ExperiencesHomepageUplift/ExperiencesHomepageRecentSearches'
import ExperiencesHomepageLuxPlusTopOffers from './ExperiencesHomepageUplift/ExperiencesHomepageLuxPlusTopOffers'
import ExperiencesHomepageBrowseByCategory from './ExperiencesHomepageUplift/ExperiencesHomepageBrowseByCategory'
import ExperiencesHomepageBrowseByTrend from './ExperiencesHomepageUplift/ExperiencesHomepageBrowseByTrend'
import ExperiencesHomepageTopPicks from './ExperiencesHomepageUplift/ExperiencesHomepageTopPicks'
import ExperiencesHomepageDiscoverNear from './ExperiencesHomepageUplift/ExperiencesHomepageDiscoverNear'

const FirstSection = styled(LayoutContainer)`
  padding-top: ${rem(18)};
  padding-bottom: ${rem(20)};
  position: relative;
  z-index: 0;

  > * + * {
    margin-top: ${rem(32)};
  }
`

const SecondSection = styled(LayoutContainer)`
  padding-top: ${rem(0)};
`

const SectionPane = styled(Pane)`
  padding-block: ${rem(20)};
  padding-inline: 0;
`

const LocationPicker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)} ${rem(20)};
    transform: translateX(-50%) translateY(-50%);
    position: absolute;
    top: 0;
    inset-inline-start: 50%;
    box-shadow: ${props => props.theme.shadow.bottom.small};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    gap: ${rem(12)};
    border-radius: ${props => props.theme.borderRadius.M};
    flex-direction: row;
    align-items: center;
  }

  [dir='rtl'] & {
    ${mediaQueryUp.tablet} {
      transform: translateX(50%) translateY(-50%);
    }
  }
`

const BannerContainer = styled.div`
  padding-block: ${rem(20)};
  padding-inline: 0;
`

interface MappedProps {
  experiencePlace: App.ExperiencePlace
  banners?: Array<App.ListBanner>
  isLuxPlusExperiencesEnabled: boolean
}

interface Props extends MappedProps {
}

function ExperiencesPage(props: Props) {
  const { experiencePlace, banners, isLuxPlusExperiencesEnabled } = props
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const categoryCarousels = useCategoryCarousels()

  const { globalExperiencesSearchDispatch, globalExperiencesSearchState } = useGlobalExperiencesSearchContext()
  const locations = getExperiencesPageLocations(geo.currentRegionCode.toLowerCase())

  const headerRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const currentPlace = useSmartExperiencePlace()
  const { isAnyVariant: useExperiencesHomepageUplift } = useExperiment(
    ChertoExperiments.experiencesHomepageUplift,
    { canActivate: !!currentPlace },
  )
  const showModal = useContext(ModalContext)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  useEffect(() => {
    if (useExperiencesHomepageUplift) {
      dispatch(fetchExperienceMetrics())
    }
  }, [dispatch, useExperiencesHomepageUplift])

  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_EXPERIENCES))
  }, [dispatch])

  const queryParams = useQueryParams()
  const carouselName = queryParams.get('carousel')

  useEffect(() => {
    if (!carouselName) return

    const headingText = getExperienceCarouselHeadingText(intl, carouselName)
    if (!headingText) return

    const findHeadingByText = (text: string): HTMLElement | null => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
      while (walker.nextNode()) {
        if (walker.currentNode.textContent?.trim() === text) {
          return walker.currentNode.parentElement
        }
      }
      return null
    }

    const controller = new AbortController()
    waitElement(() => findHeadingByText(headingText), 15, 500)
      .then(el => {
        if (!controller.signal.aborted) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      .catch(() => {}) // element not found after retries
    return () => controller.abort()
  }, [carouselName, intl])

  const showSearch = useCallback(() => {
    globalExperiencesSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Locations })
    headerRef.current?.scrollIntoView({ block: 'center' })
  }, [globalExperiencesSearchDispatch])

  const editorPickIdentities = useMemo(() => {
    const baseLocations = getEditorPickLocations()
    return take(baseLocations.filter(loc => loc.id !== currentPlace?.id), 3)
  }, [currentPlace?.id])
  const resolvedEditorPickIdentities = useResolvedPlaceIdentities(editorPickIdentities)
  const resolvedEditorPickPlaces = useMemo(
    () => resolvedEditorPickIdentities.filter((place): place is App.Place => !!place),
    [resolvedEditorPickIdentities],
  )

  const openLocationModal = useCallback(() => {
    showModal(<ExperiencesPageLocationModal
      onSearchByDestination={showSearch}
    />)
  }, [showModal, showSearch])

  const geoFilteredBanners = useMemo(() => {
    if (!banners?.length) return []
    const stateName = geo.geoStateName?.toLowerCase()
    return banners.filter(banner => {
      if (!banner.geoStates?.length) return true
      if (!stateName) return false
      return banner.geoStates.some(s => s.toLowerCase() === stateName)
    })
  }, [banners, geo.geoStateName])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Recently Viewed Carousel'),
  )

  const luxPlusTracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Discounted experiences'),
  )

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <GlobalSearchContextProvider
      state={globalExperiencesSearchState}
      dispatch={globalExperiencesSearchDispatch}
    >
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <ExperiencesPageSearchHeader ref={headerRef} />
      {currentPlace && (useExperiencesHomepageUplift ? (
        <ExperiencesHomepageUpliftLayout
          metrics={<ExperiencesHomepageMetrics />}
          personalisation={<ExperiencesHomepagePersonalisation />}
          recentSearches={<ExperiencesHomepageRecentSearches />}
          luxPlusTopOffers={<ExperiencesHomepageLuxPlusTopOffers />}
          browseByCategory={<ExperiencesHomepageBrowseByCategory />}
          browseByTrend={<ExperiencesHomepageBrowseByTrend />}
          topPicks={<ExperiencesHomepageTopPicks />}
          discoverNear={<ExperiencesHomepageDiscoverNear />}
        />
      ) : (
        <>
          <Pane>
            <FirstSection>
              <VerticalSpacer gap={8}>
                <LocationPicker>
                  <BodyText variant="large" weight="bold" wrap="no-wrap">
                    {intl.formatMessage({
                      id: 'HirBn9nKQeUZhE5y37tG',
                      defaultMessage: 'Discover top experiences around',
                      description: 'Heading text for discovering top experiences near user location on experiences landing page',
                    })}
                  </BodyText>
                  <TextButton
                    kind="secondary"
                    onClick={openLocationModal}
                    startIcon={<LineMapMarkerIcon />}
                    endIcon={<LineAngleDownIcon />}
                  >
                    {currentPlace.name}
                  </TextButton>
                </LocationPicker>
                <OfferListEventsProvider tracking={tracking}>
                  <ExperiencesPageRecentlyViewedExperiences />
                </OfferListEventsProvider>
              </VerticalSpacer>
              <ExperiencesPageUpcomingOrderCarousels />
            </FirstSection>
          </Pane>
          <SectionPane>
            <LayoutContainer>
              <VerticalSpacer gap={2}>
                <ExperiencesPageFeaturedCarousel place={currentPlace} />
                {!!geoFilteredBanners.length && <BannerContainer>
                  <BannerCarousel banners={geoFilteredBanners} />
                </BannerContainer>}
              </VerticalSpacer>
            </LayoutContainer>
          </SectionPane>
          {!!isLuxPlusExperiencesEnabled && <Pane>
            <OfferListEventsProvider tracking={luxPlusTracking}>
              <ExperienceLuxPlusCarousel place={currentPlace} subtitle="" />
            </OfferListEventsProvider>
          </Pane>}
          <Pane>
            <SecondSection>
              <VerticalSpacer gap={16}>
                <ExperiencesPageCategoriesCarousel place={currentPlace} />
                <ExperiencesPageWhiteLabelBannerAd />
              </VerticalSpacer>
              <ExperiencesPageSearchCarousel
                title={intl.formatMessage({
                  id: 'YLY5zQit8SZCj6G8-48j',
                  defaultMessage: 'Our exclusive experiences',
                  description: 'Carousel heading for exclusive experiences section on experiences landing page',
                })}
                parentCategoryCode={40}
                primaryPlace={currentPlace}
                sortType={config.BRAND === 'luxuryescapes' ? undefined : 'type'}
                filterMode={experiencePlace.userSelected ? 'category' : 'place'}
              />
              {take(categoryCarousels, 2).map(carousel =>
                <ExperiencesPageSearchCarousel
                  key={carousel.code}
                  title={carousel.title}
                  parentCategoryCode={carousel.code}
                  primaryPlace={currentPlace}
                  sortType={config.BRAND === 'luxuryescapes' ? undefined : 'type'}
                  filterMode={experiencePlace.userSelected ? 'category' : 'place'}
                />)}
            </SecondSection>
          </Pane>
          {locations.length > 0 && <SectionPane>
            <LayoutContainer>
              <VerticalSpacer gap={32}>
                <ExperiencesTrendingDestinationsCarousel locations={locations} />
                <Group gap={4} direction="horizontal" verticalAlign="center">
                  <BodyText variant="large">
                    {intl.formatMessage({
                      id: 'gv3RrQrXPeMYN1WTz62N',
                      defaultMessage: 'Have a place in mind?',
                      description: 'Prompt text encouraging users to search by destination on experiences landing page',
                    })}
                  </BodyText>
                  <TextButton
                    kind="secondary"
                    startIcon={<SolidSearchIcon />}
                    onClick={showSearch}
                  >
                    {intl.formatMessage({
                      id: '6wom2JLE3H_j66wHuMU-',
                      defaultMessage: 'Search by destination',
                      description: 'Button text to search experiences by destination on experiences landing page',
                    })}
                  </TextButton>
                </Group>
              </VerticalSpacer>
            </LayoutContainer>
          </SectionPane>}
          <SectionPane>
            <LayoutContainer>
              <ExperiencesPageCollectionsCarousels place={currentPlace} />
            </LayoutContainer>
          </SectionPane>
          <Pane>
            <LayoutContainer>
              {take(categoryCarousels, 1000, 2).map(carousel =>
                <ExperiencesPageSearchCarousel
                  key={carousel.code}
                  parentCategoryCode={carousel.code}
                  primaryPlace={currentPlace}
                  title={carousel.title}
                  sortType={config.BRAND === 'luxuryescapes' ? undefined : 'type'}
                  filterMode={experiencePlace.userSelected ? 'category' : 'place'}
                />)}
            </LayoutContainer>
          </Pane>
          {config.BRAND === 'luxuryescapes' && <SectionPane>
            <LayoutContainer>
              <ExperiencesPageBlogPostsCarousel />
            </LayoutContainer>
          </SectionPane>}
          {resolvedEditorPickPlaces.length > 0 && <SectionPane background="light-background">
            <LayoutContainer>
              <ExperiencesPageEditorsPicks locations={resolvedEditorPickPlaces} />
            </LayoutContainer>
          </SectionPane>}
        </>
      ))}
    </GlobalSearchContextProvider>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State) {
  return {
    experiencePlace: state.experience.experiencePlace,
    banners: state.content.listBanners?.experiences,
    isLuxPlusExperiencesEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperiencesPage)
